import { useMutation, useQueryClient, type UseMutationOptions } from "@tanstack/react-query";
import { supabase, type Funciones } from "./supabase";
import { mapearErrorSupabase, registrarError, type ErrorApp } from "./errores";
import { useToast } from "@/components/ui/Toast";

type NombreFn = keyof Funciones & string;
type Args<F extends NombreFn> = Funciones[F]["Args"];
type Ret<F extends NombreFn> = Funciones[F]["Returns"];

/** Claves de query que se invalidan tras cualquier mutación de negocio. */
export const CLAVES_DOMINIO = ["existencias", "historial", "hoy", "zonas", "productos", "importaciones", "pendientes", "almacenes", "empresa"];

/**
 * Envuelve una función SQL: llama rpc(), traduce el error, muestra toast y refresca
 * todas las queries del dominio (A8 del análisis legacy: nada se queda viejo).
 */
export function useRpc<F extends NombreFn>(
  nombre: F,
  opciones: Omit<UseMutationOptions<Ret<F>, ErrorApp, Args<F>>, "mutationFn"> & { silencioso?: boolean } = {},
) {
  const qc = useQueryClient();
  const toast = useToast();
  const { silencioso, ...resto } = opciones;
  return useMutation<Ret<F>, ErrorApp, Args<F>>({
    mutationFn: async (args) => {
      const { data, error } = await supabase.rpc(nombre, args as never);
      if (error) throw mapearErrorSupabase(error);
      return data as Ret<F>;
    },
    ...resto,
    onSuccess: async (data, vars, onMutateResult, ctx) => {
      await Promise.all(CLAVES_DOMINIO.map((k) => qc.invalidateQueries({ queryKey: [k] })));
      await resto.onSuccess?.(data, vars, onMutateResult, ctx);
    },
    onError: (error, vars, onMutateResult, ctx) => {
      registrarError(nombre, error, { args: vars });
      if (!silencioso) toast.error(error.mensaje);
      resto.onError?.(error, vars, onMutateResult, ctx);
    },
  });
}

/** Ejecuta un rpc fuera de React Query (para flujos por fila, como el conteo). */
export async function rpc<F extends NombreFn>(nombre: F, args: Args<F>): Promise<Ret<F>> {
  const { data, error } = await supabase.rpc(nombre, args as never);
  if (error) throw mapearErrorSupabase(error);
  return data as Ret<F>;
}
